import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken, getLegacyUrl } from "../../api";

import Swal from "sweetalert2";

import CheckerViewShell from "./CheckerViewShell";

function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
}

export default function CheckerPendingAssignmentView({
  title = "Pending Assignment",
  intro = "Assign pending processor invoices to available processors by team while continuing to use the current Django assignment flow.",
  endpoint = "/api/get-UnassignedInvoices-team/",
  legacySection = "pendinginvoices",
  legacyButtonLabel = "Open Legacy Assignment Page",
  loadingLabel = "Loading pending assignments…",
  emptyLabel = "No invoices found in the pending assignment queue.",
  queueLabel = "pending assignments",
  queueNote = "Invoices waiting for processor assignment.",
  unassignedNote = "Invoices that do not currently have a processor selected.",
}) {
  const [rows, setRows] = useState([]);
  const [processors, setProcessors] = useState({});
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  

  const summary = useMemo(() => {
    const teams = new Set(rows.map((row) => row.team_assigned).filter(Boolean));
    const unassigned = rows.filter((row) => !row.processor_assigned_id).length;

    return {
      count: rows.length,
      teams: teams.size,
      unassigned,
    };
  }, [rows]);

  

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });

      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || `Unable to load ${title.toLowerCase()}.`);
      }

      const nextRows = payload.data || [];
      const nextProcessors = payload.processors || {};
      const nextAssignments = {};

      nextRows.forEach((row) => {
        nextAssignments[row.InvoiceGroupKey] = row.processor_assigned_id ? String(row.processor_assigned_id) : "";
      });

      setRows(nextRows);
      setProcessors(nextProcessors);
      setAssignments(nextAssignments);
    } catch (requestError) {
      setError(requestError.message || `Unable to load ${title.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  async function handleSubmit(row) {
    const selectedProcessorId = assignments[row.InvoiceGroupKey];
    const selectedProcessor = (processors[row.team_assigned] || []).find(
      (processor) => String(processor.id) === String(selectedProcessorId)
    );
    if (!selectedProcessor) {
      setError("Please select a processor before submitting the assignment.");
      return;
    }

    setStatus("");
    setError("");
    setActionKey(row.InvoiceGroupKey);

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/assign-processor/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          InvoiceGroupKey: row.InvoiceGroupKey,
          processor_id:   selectedProcessorId,
          processor_email: selectedProcessor.email,
        }),
      });

      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Unable to assign processor.");
      }

      setStatus(`Processor assigned successfully for invoice ${row.InvoiceId}.`);
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || "Unable to assign processor.");
    } finally {
      setActionKey("");
    }
  }

  function openLegacyAssignmentPage() {
    window.localStorage.setItem("activeApprovalSection", legacySection);
    window.open(getLegacyUrl("/checker-dashboard/"), "_blank", "noreferrer");
  }

  function refreshPendingActions() {
    loadRows();
  }

  return (
    <CheckerViewShell
          title="Assign available invoices to processors"
          intro=""
          metrics={[
            { label: "Queue", value: summary.count },
            { label: "Teams", value: summary.teams },
            { label: "Unassigned", value: summary.unassigned },
            
          ]}
          actions={
            <button
              className="ghost-button processor-view__refresh"
              type="button"
              onClick={refreshPendingActions}
            >
              Refresh Pending Actions
            </button>
          }
        >
          <section className="processor-view">
            

            {status ? <div className="config-banner config-banner--success">{status}</div> : null}
            {error ? <div className="config-banner config-banner--error">{error}</div> : null}

            

            <div className="workspace-panel">
              

              {loading ? (
                <p className="config-note">{loadingLabel}</p>
              ) : (
                <div className="config-table processor-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice ID</th>
                        <th>Vendor Name</th>
                        <th>Invoice No.</th>
                        <th>Invoice Date</th>
                        <th>Invoice Value</th>
                        <th>Team</th>
                        <th>File</th>
                        <th>Assign To</th>
                        <th>Submit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? (
                        rows.map((row) => (
                          <tr key={row.InvoiceGroupKey}>
                            <td>{row.InvoiceId}</td>
                            <td>{row.VendorName || "-"}</td>
                            <td>{row.InvoiceNo || "-"}</td>
                            <td>{formatDate(row.InvoiceDate)}</td>
                            <td>{formatAmount(row.InvoiceValue)}</td>
                            <td>{row.team_assigned || "-"}</td>
                            <td>
                              {row.InvoiceId ? (
                                <a
                                  href={getLegacyUrl(`/api/invoice/view/${row.InvoiceId}/`)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  PDF
                                </a>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <select
                                value={assignments[row.InvoiceGroupKey] || ""}
                                onChange={(event) =>
                                  setAssignments((current) => ({
                                    ...current,
                                    [row.InvoiceGroupKey]: event.target.value,
                                  }))
                                }
                              >
                                <option value="">-- Select --</option>
                                {(processors[row.team_assigned] || []).map((processor) => (
                                  <option key={processor.id} value={processor.id}>
                                    {processor.email || processor.username}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <button
                                className="primary-button processor-action-button"
                                type="button"
                                disabled={actionKey === row.InvoiceGroupKey}
                                onClick={() => handleSubmit(row)}
                              >
                                {actionKey === row.InvoiceGroupKey ? "Submitting..." : "Submit"}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9">{emptyLabel}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
    </CheckerViewShell>
  );
}
